// Shared presentation constants for the pipeline-canvas nodes. Same visual
// language as the generator-profile canvas (sharp-cornered bordered cards with
// a hard shadow) but sized for the wider pipeline cards and coloured per
// checkpoint type so the graph reads at a glance.

import { CheckpointType } from '../../../../api/structs';

export const nodeShell =
  'w-[260px] border bg-[var(--surface-1)] text-[var(--app-text)] shadow-[3px_3px_0px_0px_var(--border-strong)] transition-colors duration-150';

export const nodeShellSelected =
  'border-[var(--accent-strong)] ring-2 ring-[var(--accent-strong)] ring-offset-0';
export const nodeShellIdle =
  'border-[var(--border-strong)] hover:border-[var(--accent-strong)] hover:ring-1 hover:ring-[var(--accent-strong)]/60 ring-offset-0';

export const smallNodeShell =
  'w-[190px] border bg-[var(--surface-1)] text-[var(--app-text)] shadow-[3px_3px_0px_0px_var(--border-strong)] transition-colors duration-150';

export const nodeHeader =
  'px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] border-b border-[var(--border-subtle)] bg-[var(--surface-2)] flex items-center gap-1.5';

export const nodeBody = 'px-2.5 py-1.5 text-[11px] text-[var(--app-text-muted)] space-y-1';

export const handleClass =
  '!static !h-2.5 !w-2.5 !min-h-0 !min-w-0 !translate-x-0 !translate-y-0 !border !border-[var(--border-strong)] !bg-[var(--accent-strong)]';

/** Handle rows are laid out in normal flow, so the dot needs no absolute offset. */
export const handleRow =
  'flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-[var(--app-text-muted)] leading-tight';

/** The `+ input` / `+ asset` affordance rows — muted until hovered. */
export const placeholderRow =
  'opacity-60 hover:opacity-100 hover:text-[var(--accent-strong)] cursor-pointer transition-opacity';

/** Hollow dot, so a `+` pin never reads like a wired one. */
export const placeholderHandleClass =
  '!static !h-2.5 !w-2.5 !min-h-0 !min-w-0 !translate-x-0 !translate-y-0 !border !border-dashed !border-[var(--accent-strong)] !bg-transparent';

export const badgeClass =
  'inline-flex items-center gap-1 border border-[var(--border-subtle)] bg-[var(--surface-3)] px-1 py-px text-[9px] uppercase tracking-wider';

interface CheckpointTypeStyle {
  icon: string;
  accent: string;
  label: string;
}

const TYPE_STYLES: Record<string, CheckpointTypeStyle> = {
  prompt: { icon: '✎', accent: '#c084fc', label: 'prompt' },
  generator: { icon: '✦', accent: '#34d399', label: 'generator' },
  distributor: { icon: '⑃', accent: '#38bdf8', label: 'distributor' },
  sequencer: { icon: '⭣', accent: '#22d3ee', label: 'sequential distributor' },
  connector: { icon: '⑂', accent: '#fbbf24', label: 'connector' },
  upload: { icon: '⬆', accent: '#f472b6', label: 'upload' },
  tool: { icon: '⚒', accent: '#a3e635', label: 'tool' },
  // Same mark and colour family (rose) as the list view's CheckpointCard.
  agent: { icon: '❖', accent: '#fb7185', label: 'agent' },
};

export const checkpointTypeStyle = (type?: CheckpointType | string): CheckpointTypeStyle =>
  TYPE_STYLES[(type as string) || 'prompt'] || TYPE_STYLES.prompt;
